// src/components/SubmissionHistory.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaFilePdf, FaFileExcel, FaPrint, FaCalendarAlt, FaFilter } from "react-icons/fa";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../api";
import "./SubmissionHistory.css";

const PER_PAGE = 20;

const MONTHS_ID = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

/* ===== Helpers tanggal ===== */
function firstDayStr(y, mIndex) {
  const mm = String(mIndex + 1).padStart(2, "0");
  return `${y}-${mm}-01`;
}
function lastDayStr(y, mIndex) {
  const last = new Date(y, mIndex + 1, 0).getDate();
  const mm = String(mIndex + 1).padStart(2, "0");
  const dd = String(last).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}
function parseDateFlexible(s) {
  if (!s) return null;

  let d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d;

  // dd/mm/yyyy atau dd-mm-yyyy
  const m = String(s).match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    d = new Date(`${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}T00:00:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}
function isInMonth(dateStr, y, mIndex) {
  const d = parseDateFlexible(dateStr);
  if (!d) return false;
  return d.getFullYear() === y && d.getMonth() === mIndex;
}
function fmtDateOnly(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("id-ID");
}

/* ===== Status ===== */
function normalizeStatus(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (["submitted", "pending", "menunggu"].includes(s)) return "Pending";
  if (["approved", "disetujui"].includes(s)) return "Approved";
  if (["shipped", "dikirim", "sedang dikirim", "dalam pengiriman"].includes(s)) return "Shipped";
  if (["delivered", "received", "diterima", "telah diterima", "selesai"].includes(s)) return "Delivered";
  if (["rejected", "ditolak"].includes(s)) return "Rejected";
  return "Unknown";
}

function badgeClass(ui) {
  switch (ui) {
    case "Pending":
      return "warn";
    case "Approved":
      return "info";
    case "Shipped":
      return "primary";
    case "Delivered":
      return "ok";
    case "Rejected":
      return "danger";
    default:
      return "muted";
  }
}

function statusLabel(ui) {
  switch (ui) {
    case "Pending":
      return "Diajukan";
    case "Approved":
      return "Disetujui";
    case "Shipped":
      return "Telah dikirimkan";
    case "Delivered":
      return "Telah diterima";
    case "Rejected":
      return "Ditolak";
    default:
      return "Tidak diketahui";
  }
}

export default function SubmissionHistory({ historyData, onViewDetail, userRole }) {
  const location = useLocation();
  const navigate = useNavigate();

  const onAdminRoute = location.pathname.startsWith("/admin");
  const isAdmin =
    String(userRole || "").toLowerCase() === "admin" ||
    onAdminRoute ||
    (localStorage.getItem("role") || "").toLowerCase() === "admin";

  /* ===== Filters ===== */
  const [monthIdx, setMonthIdx] = useState(now.getMonth());
  const [year, setYear] = useState(CURRENT_YEAR);
  const [workAreaFilter, setWorkAreaFilter] = useState("all"); // admin only

  /* ===== Data ===== */
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(!Array.isArray(historyData));
  const [fetching, setFetching] = useState(false);

  /* ===== Pagination ===== */
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const periodKey = `${year}-${monthIdx}`;
  const abortRef = useRef(null);
  const reqIdRef = useRef(0);

  const allCacheRef = useRef(new Map()); // key: `${periodKey}:${scope}` -> array

  const takeNumber = (r) =>
    r?.number || r?.submission_number || r?.cart_number || r?.request_number || r?.id || null;

  const takeDateStr = (r) => r?.date || r?.created_at || r?.submitted_at || r?.updated_at || null;

  const getTotalQuantity = (submission) => {
    if (Number.isFinite(Number(submission?.total_items))) return Number(submission.total_items);
    const items = submission?.items;
    if (!Array.isArray(items)) return 0;
    return items.reduce((sum, it) => sum + (Number(it?.qty) || 0), 0);
  };

  const productText = (submission) => {
    if (submission?.product_names) return submission.product_names;
    const items = submission?.items;
    if (!Array.isArray(items) || items.length === 0) return "Tidak ada item";
    return items.map((it) => it?.name).filter(Boolean).join(", ");
  };

  /* ===== Reset pagination saat filter berubah ===== */
  useEffect(() => {
    setPage(1);
  }, [monthIdx, year, workAreaFilter]);

  /* ===================== FETCH ===================== */
  useEffect(() => {
    const startStr = firstDayStr(year, monthIdx);
    const endStr = lastDayStr(year, monthIdx);

    // mode props (client-side)
    if (Array.isArray(historyData)) {
      setErr("");
      setLoading(false);
      setFetching(false);

      let filtered = historyData.filter((r) => isInMonth(takeDateStr(r), year, monthIdx));
      if (isAdmin && workAreaFilter !== "all") {
        const target = String(workAreaFilter).trim().toLowerCase();
        filtered = filtered.filter((r) => String(r?.work_area || "").trim().toLowerCase() === target);
      }

      const totalFiltered = filtered.length;
      const lp = Math.max(1, Math.ceil(totalFiltered / PER_PAGE));
      const safePage = Math.min(page, lp);

      const slice = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);
      const merged = slice.map((r) => ({ ...r, __uiStatus: normalizeStatus(r.status) }));

      setRows(merged);
      setTotal(totalFiltered);
      setLastPage(lp);
      return;
    }

    // server mode
    const reqId = ++reqIdRef.current;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const fetchPage = async (params) => {
      const res = await api.get("/submissions", { params, signal: controller.signal });
      const data = res?.data;
      const headers = res?.headers;

      const arr = Array.isArray(data?.data) ? data.data : [];
      const meta = data?.meta || data?.pagination || null;

      let last = 1;
      let tot = arr.length;

      if (meta && Number(meta.total) >= 0) {
        last = Number(meta.last_page || 1);
        tot = Number(meta.total || arr.length);
      } else {
        const headerTotal = Number(headers?.["x-total-count"]);
        tot = Number.isFinite(headerTotal) ? headerTotal : arr.length;
        last = Math.max(1, Math.ceil(tot / (params.per_page || PER_PAGE)));
      }

      return { arr, last, tot };
    };

    const fetchAllForPeriod = async () => {
      const uid = localStorage.getItem("user_id");
      const scopeKey = isAdmin ? "admin_all" : `mine_${uid || "nouid"}`;
      const key = `${periodKey}:${scopeKey}`;

      const cached = allCacheRef.current.get(key);
      if (Array.isArray(cached)) return cached;

      const per = 200;
      let p = 1;
      let all = [];
      let guard = 80;

      while (guard-- > 0) {
        const params = {
          page: p,
          per_page: per,
          status: "all",
          ...(isAdmin ? { scope: "all" } : { scope: "mine", ...(uid ? { user_id: uid } : {}) }),
          month: monthIdx + 1,
          year,
          start_date: startStr,
          end_date: endStr,
          date_from: startStr,
          date_to: endStr,
        };

        const { arr, last } = await fetchPage(params);
        all = all.concat(arr);

        if (p >= last) break;
        if (arr.length < per) break;
        p += 1;
      }

      all = all.filter((r) => isInMonth(takeDateStr(r), year, monthIdx));
      allCacheRef.current.set(key, all);
      return all;
    };

    (async () => {
      try {
        setErr("");
        setFetching(true);
        if (rows.length === 0) setLoading(true);

        const uid = localStorage.getItem("user_id");
        const baseParams = {
          page,
          per_page: PER_PAGE,
          status: "all",
          ...(isAdmin ? { scope: "all" } : { scope: "mine", ...(uid ? { user_id: uid } : {}) }),
          month: monthIdx + 1,
          year,
          start_date: startStr,
          end_date: endStr,
          date_from: startStr,
          date_to: endStr,
        };

        // === Admin + filter wilayah kerja ===
        if (isAdmin && workAreaFilter !== "all") {
          const tryParams = { ...baseParams, work_area: workAreaFilter };
          const { arr, last, tot } = await fetchPage(tryParams);

          const target = String(workAreaFilter).trim().toLowerCase();
          const looksFiltered =
            arr.length === 0 || arr.every((r) => String(r?.work_area || "").trim().toLowerCase() === target);

          if (looksFiltered) {
            if (reqId !== reqIdRef.current) return;
            setRows(arr.map((r) => ({ ...r, __uiStatus: normalizeStatus(r.status) })));
            setTotal(tot);
            setLastPage(last);
            return;
          }

          const all = await fetchAllForPeriod();
          const filteredAll = all
            .filter((r) => String(r?.work_area || "").trim().toLowerCase() === target)
            .map((r) => ({ ...r, __uiStatus: normalizeStatus(r.status) }));

          const totalFiltered = filteredAll.length;
          const lp = Math.max(1, Math.ceil(totalFiltered / PER_PAGE));
          const safePage = Math.min(page, lp);
          const slice = filteredAll.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

          if (reqId !== reqIdRef.current) return;
          setRows(slice);
          setTotal(totalFiltered);
          setLastPage(lp);
          return;
        }

        // === Normal ===
        const { arr, last, tot } = await fetchPage(baseParams);
        let finalArr = arr;

        if (finalArr.length && !finalArr.every((r) => isInMonth(takeDateStr(r), year, monthIdx))) {
          finalArr = finalArr.filter((r) => isInMonth(takeDateStr(r), year, monthIdx));
        }

        if (reqId !== reqIdRef.current) return;
        setRows(finalArr.map((r) => ({ ...r, __uiStatus: normalizeStatus(r.status) })));
        setTotal(tot);
        setLastPage(last);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        if (reqId !== reqIdRef.current) return;
        setErr(e?.response?.data?.message || "Tidak dapat memuat data pengajuan.");
        setLastPage((p) => (p > 0 ? p : 1));
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setFetching(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyData, isAdmin, monthIdx, year, page, workAreaFilter]);

  /* ===== opsi wilayah kerja (admin) ===== */
  const workAreaOptions = useMemo(() => {
    if (!isAdmin) return [];
    const set = new Set();
    rows.forEach((r) => {
      const wa = String(r?.work_area || "").trim();
      if (wa) set.add(wa);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
  }, [rows, isAdmin]);

  const bulanLabel = `${MONTHS_ID[monthIdx]} ${year}`;

  const handleReset = () => {
    setMonthIdx(now.getMonth());
    setYear(CURRENT_YEAR);
    setWorkAreaFilter("all");
    setPage(1);
  };

  /* ===================== Export / Print ===================== */
  const tableRef = useRef(null);

  const captureTableCanvas = useCallback(async () => {
    const node = tableRef.current;
    if (!node) return null;

    const wrap = node.querySelector(".sb-tableWrap");
    const prev = wrap
      ? {
          maxHeight: wrap.style.maxHeight,
          overflow: wrap.style.overflow,
        }
      : null;

    if (wrap) {
      wrap.style.maxHeight = "none";
      wrap.style.overflow = "visible";
    }

    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      return canvas;
    } finally {
      if (wrap && prev) {
        wrap.style.maxHeight = prev.maxHeight || "";
        wrap.style.overflow = prev.overflow || "";
      }
    }
  }, []);

  const handleDownloadPDF = async () => {
    const canvas = await captureTableCanvas();
    if (!canvas) return;

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "pt", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`history_pengajuan_${year}_${String(monthIdx + 1).padStart(2, "0")}.pdf`);
  };

  const handleDownloadExcel = () => {
    // ✅ kolom "Satuan" sudah dihapus
    const excelRows = rows.map((r) => ({
      "Nomor Pengajuan": takeNumber(r) || "",
      Tanggal: fmtDateOnly(takeDateStr(r)),
      "Jumlah Item": getTotalQuantity(r),
      "Produk Diajukan": productText(r),
      ...(isAdmin ? { Nama: r.user_name || "", "Wilayah Kerja": r.work_area || "" } : {}),
      Status: statusLabel(r.__uiStatus || normalizeStatus(r.status)),
    }));

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "HistoryPengajuan");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `history_pengajuan_${year}_${String(monthIdx + 1).padStart(2, "0")}.xlsx`
    );
  };

  const handlePrint = async () => {
    const canvas = await captureTableCanvas();
    if (!canvas) return window.print();
    const imgData = canvas.toDataURL("image/png");

    const w = window.open("", "_blank");
    if (!w) return alert("Popup diblokir browser. Izinkan popup untuk mencetak.");

    w.document.open();
    w.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Print Daftar Pengajuan</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            html, body { margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; background: #fff; }
            img { width: 100%; height: auto; display: block; }
          </style>
        </head>
        <body>
          <img id="print-img" src="${imgData}" />
          <script>
            window.onload = function(){ window.print(); }
          </script>
        </body>
      </html>
    `);
    w.document.close();
  };

  const openDetail = (submission) => {
    if (typeof onViewDetail === "function") return onViewDetail(submission);
    const number = takeNumber(submission);
    if (!number) return;
    navigate(isAdmin ? `/admin/submissions/${encodeURIComponent(number)}` : `/submissions/${encodeURIComponent(number)}`);
  };

  const canPrev = page > 1 && !loading && !fetching;
  const canNext = page < lastPage && !loading && !fetching;

  return (
    <div className="sb-page">
      <div className="sb-wrap">
        <div className="sb-head">
          <div>
            <h2 className="sb-title">Daftar Pengajuan</h2>
            <div className="sb-subtitle">
              Periode: <b>{bulanLabel}</b> — Total data: <b>{total}</b> • Tampil: <b>{rows.length}</b>
              {fetching ? <span style={{ marginLeft: 8 }}>• Memuat…</span> : null}
            </div>
          </div>
        </div>

        <div className="sb-toolbar">
          <div className="sb-filter">
            <FaCalendarAlt className="sb-ic" />
            <select className="sb-select" value={monthIdx} onChange={(e) => setMonthIdx(Number(e.target.value))}>
              {MONTHS_ID.map((m, idx) => (
                <option key={m} value={idx}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div className="sb-filter">
            <FaCalendarAlt className="sb-ic" />
            <select className="sb-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div className="sb-filter">
              <FaFilter className="sb-ic" />
              <select className="sb-select" value={workAreaFilter} onChange={(e) => setWorkAreaFilter(e.target.value)}>
                <option value="all">Semua Wilayah</option>
                {workAreaOptions.map((wa) => (
                  <option key={wa} value={wa}>
                    {wa}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button type="button" className="sb-btn sb-btn-light sb-btn-sm" onClick={handleReset}>
            Reset
          </button>

          <div className="sb-actions">
            <button type="button" className="sb-btn sb-btn-danger" onClick={handleDownloadPDF} disabled={loading}>
              <FaFilePdf /> DOWNLOAD PDF
            </button>
            <button type="button" className="sb-btn sb-btn-success" onClick={handleDownloadExcel} disabled={loading}>
              <FaFileExcel /> DOWNLOAD EXCEL
            </button>
            <button type="button" className="sb-btn sb-btn-primary" onClick={handlePrint} disabled={loading}>
              <FaPrint /> PRINT
            </button>
          </div>
        </div>

        {err ? <div className="sb-alert">{err}</div> : null}

        <div className="sb-card" ref={tableRef}>
          <div className="sb-tableWrap">
            <table className={`sb-table ${isAdmin ? "admin" : "user"}`}>
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Nomor Pengajuan</th>
                  <th style={{ width: 140 }}>Tanggal</th>
                  <th style={{ width: 120 }}>Jumlah Item</th>
                  <th>Produk Diajukan</th>
                  {isAdmin ? <th style={{ width: 170 }}>Nama</th> : null}
                  {isAdmin ? <th style={{ width: 200 }}>Wilayah Kerja</th> : null}
                  <th style={{ width: 170 }}>Status</th>
                  <th className="sb-center" style={{ width: 140 }}>
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td className="sb-center" colSpan={isAdmin ? 8 : 6} style={{ padding: 18 }}>
                      Memuat…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="sb-center" colSpan={isAdmin ? 8 : 6} style={{ padding: 18 }}>
                      Belum ada data pengajuan untuk {bulanLabel}.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const ui = r.__uiStatus || normalizeStatus(r.status);
                    const number = takeNumber(r);

                    return (
                      <tr key={`${number || "row"}-${idx}`}>
                        <td className="sb-mono">{number || "N/A"}</td>
                        <td>{fmtDateOnly(takeDateStr(r))}</td>
                        <td className="sb-mono">{getTotalQuantity(r)}</td>

                        <td title={productText(r)} className="sb-ellipsis">
                          {productText(r)}
                        </td>

                        {isAdmin ? <td className="sb-ellipsis">{r.user_name || "-"}</td> : null}
                        {isAdmin ? <td className="sb-ellipsis">{r.work_area || "-"}</td> : null}

                        <td>
                          <span className={`sb-badge ${badgeClass(ui)}`}>{statusLabel(ui)}</span>
                        </td>

                        <td className="sb-center">
                          <button
                            type="button"
                            className="sb-btn sb-btn-outline sb-btn-sm"
                            onClick={() => openDetail(r)}
                          >
                            <FaEye /> Detail
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="sb-pager">
            <button
              type="button"
              className="sb-btn sb-btn-light sb-btn-sm"
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>

            <div className="sb-pagerInfo">
              Halaman <b>{page}</b> / <b>{lastPage}</b>
            </div>

            <button
              type="button"
              className="sb-btn sb-btn-light sb-btn-sm"
              disabled={!canNext}
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
